import { useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { DetailViewCard } from '../../Compounds/CardWithTitle/DetailViewCard';
import { DetailPanelContainer } from '../../Compounds/DetailPanel/DetailPanelContainer';
import { LabelValueField, EditFieldType } from '../../Compounds/DetailPanel/LabelValueField';
import { linkSectionsInfo } from './linkHelpers';
import { updateLink, Link } from '../../../store/Link';
import { getFormattedDate } from '../../../utils/helpers';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { getAssetById } from '../../../store/Asset';
import { getProvisionalAssetById } from '../../../store/ProvisionalAsset';
import { getRpmAccountById } from '../../../store/RpmAccount';
import { addDrawer, updateDrawerOpenSubject } from '../../../store/Drawer';
import { getUserSubject, User, UserRole } from '../../../store/User';
import { LinkAllocationSection } from './LinkAllocationSection';

export interface LinkDetailsParams {
  data: Link;
}

export const LinkDetails = ({ data }: LinkDetailsParams) => {
  const [user, setUser] = useState<User>();

  useEffect(() => {
    const userSub = getUserSubject().subscribe((u) => setUser(u));

    return () => {
      if (userSub) userSub.unsubscribe();
    };
  }, []);

  const saveAction = (update: Link) => updateLink(update, true);

  const handleDetailClick = async (type: string) => {
    if (type === 'rpmAccount') {
      const rpmAccount = await getRpmAccountById(String(data.rpmAccountId));
      addDrawer({
        id: v4(),
        type: 'rpmAccount',
        ...getMetaData({ name: ComponentName.RPM_ACCOUNT, id: String(data.rpmAccountId) }),
        data: rpmAccount
      });
    } else if (type === 'asset') {
      const asset = await getAssetById(data.assetId || '');
      addDrawer({
        id: v4(),
        type: 'asset',
        ...getMetaData({
          name: ComponentName.ASSET,
          sfId: data.assetSfId,
          id: data.assetId || '',
          data: data.assetId ? asset : undefined
        }),
        data: asset
      });
    } else if (type === 'provisionalAsset') {
      const provisionalAsset = await getProvisionalAssetById(data.assetId || '');
      addDrawer({
        id: v4(),
        type: 'provisionalAsset',
        ...getMetaData({ name: ComponentName.PROVISIONAL_ASSET, sfId: data.assetSfId, id: data.assetId || '' }),
        data: provisionalAsset
      });
    }
    updateDrawerOpenSubject(true);
  };

  const updateStatus = async (status: string): Promise<string | null> => {
    let candidate = data.candidate || false;
    let archived = data.archived || false;
    if (status === 'Active') {
      candidate = false;
      archived = false;
    } else if (status === 'Candidate') {
      candidate = true;
      archived = false;
    } else if (status === 'Archived') {
      archived = true;
    }
    await saveAction({ ...data, archived, candidate });
    return null;
  };

  return (
    <div
      className="space-y-4"
      data-testid="link-details-drawer">
      <DetailViewCard
        enableCollapse
        key="link_detail"
        title="Info">
        <div>
          <div className="w-full flex flex-wrap pt-4 px-4">
            <div className="w-1/2 flex flex-col mb-1 pr-9 -mb-2">
              <p className="font-sans text-grey-6 text-[14px] font-normal">
                {data.assetSfId ? 'Asset' : 'Provisional Asset'}
              </p>
              <button
                className="text-indigo"
                data-testid="detail-cta-button"
                onClick={() => handleDetailClick(data.assetSfId ? 'asset' : 'provisionalAsset')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '14px',
                  textAlign: 'left'
                }}>
                {data.assetSupplierName}: {data.assetNumber}
              </button>
            </div>
            <div className="w-1/2 flex flex-col mb-1 pr-9 -mb-2">
              <p className="font-sans text-grey-6 text-[14px] font-normal">RPM Account</p>
              <button
                className="text-indigo"
                data-testid="detail-cta-button"
                onClick={() => handleDetailClick('rpmAccount')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '14px',
                  textAlign: 'left'
                }}>
                {data.rpmAccountSupplierName}: {data.rpmAccountNumber}
              </button>
            </div>
          </div>
          <DetailPanelContainer
            data={linkSectionsInfo(data)}
            saveAction={(d) => saveAction(d as Link)}
          />
        </div>
      </DetailViewCard>
      <LinkAllocationSection
        link={data}
        key={data.id}
        fromDrawer
      />
      <DetailViewCard
        enableCollapse
        key="links_detail"
        title="Links">
        <div className="flex px-4 pt-4 w-full" />
      </DetailViewCard>
      <DetailViewCard
        enableCollapse
        key="meta_detail"
        title="Meta">
        <div className="flex px-4 pt-4 w-full">
          <LabelValueField
            label="ID"
            value={data.id || ''}
            field="id"
          />
          {[UserRole.ADMIN, UserRole.ENGINEER].includes(user?.role as UserRole) ? (
            <LabelValueField
              label="Status"
              value={data.archived ? 'Archived' : data.candidate ? 'Candidate' : 'Active'}
              field="status"
              canEdit={{
                field: 'status',
                type: EditFieldType.SELECT,
                options: ['Active', 'Candidate', 'Archived']
              }}
              saveValueAction={(val, _previewValue) => updateStatus(String(val))}
            />
          ) : (
            <LabelValueField
              label="Status"
              value={data.archived ? 'Archived' : data.candidate ? 'Candidate' : 'Active'}
              field="status"
            />
          )}
        </div>
        <div className="flex px-4 w-full">
          <LabelValueField
            label="Created At"
            value={getFormattedDate(data.createdAt, 'lll')}
            field="createdAt"
          />
          <LabelValueField
            label="Modified At"
            value={getFormattedDate(data.updatedAt, 'lll')}
            field="updatedAt"
          />
        </div>
        <div className="flex px-4 w-full">
          <LabelValueField
            label="Created By"
            value={`${data.createdBy?.firstName || ''} ${data.createdBy?.lastName || ''}`}
            field="createdBy.firstName"
          />
        </div>
      </DetailViewCard>
    </div>
  );
};
